import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldStar, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth-context";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function TwoFactorChallenge() {
  const { verify2fa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const challenge = location.state?.challenge_token;
  const from = location.state?.from || "/app";
  const email = location.state?.email || "";

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (!challenge) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const u = await verify2fa(challenge, code.trim());
      toast.success(`Welcome, ${u?.name || "user"}!`);
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg grid place-items-center px-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="vs-card p-8 max-w-md w-full">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center">
            <ShieldStar size={20} weight="duotone" />
          </div>
          <div>
            <div className="eyebrow">Step 2 of 2</div>
            <div className="font-display text-lg font-semibold leading-tight">Two-factor verification</div>
          </div>
        </div>
        <p className="text-sm text-[var(--vs-text-secondary)]">
          Enter the 6-digit code from your authenticator app (or a recovery code) for <span className="font-mono">{email}</span>.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="2fa-challenge-form">
          <div>
            <label className="block text-xs font-mono tracking-widest uppercase text-[var(--vs-secondary)] mb-1.5">6-digit code</label>
            <input
              data-testid="2fa-challenge-code"
              autoFocus
              inputMode="numeric"
              maxLength={20}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white font-mono text-lg tracking-[0.4em] text-center focus:outline-none focus:border-[var(--vs-secondary)] focus:ring-2 focus:ring-[var(--vs-secondary)]/20"
              placeholder="123456"
            />
          </div>

          {err && <div className="text-sm text-[var(--vs-danger)]">{err}</div>}

          <button
            type="submit"
            disabled={submitting || code.length < 4}
            data-testid="2fa-challenge-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] font-medium disabled:opacity-60"
          >
            {submitting ? "Verifying…" : "Verify and continue"} <ArrowRight size={14} />
          </button>
        </form>

        <p className="mt-4 text-[11px] text-[var(--vs-text-secondary)]">
          Lost your device? Use one of the recovery codes you saved when you set up 2FA. Each code is single-use.
        </p>
      </motion.div>
    </div>
  );
}
